import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Plane, Building2, CheckCircle2, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import LeadForm from "@/components/LeadForm";
import { Reveal } from "@/components/Reveal";
import { CallButton, GetStartedButton } from "@/components/CTA";
import { SITE } from "@/data/site";
import { LOCATIONS, getLocation } from "@/data/locations";
import { SERVICES } from "@/data/services";
import NotFoundPage from "@/pages/NotFoundPage";

const EASE = [0.22, 1, 0.36, 1];

export default function LocationPage() {
  const { slug } = useParams();
  const loc = getLocation(slug);
  if (!loc) return <NotFoundPage />;

  const idx = LOCATIONS.findIndex((l) => l.slug === slug);
  const nearby = [...LOCATIONS.slice(idx + 1), ...LOCATIONS.slice(0, idx)].slice(0, 6);

  return (
    <>
      <Seo
        title={`Passport & Visa Assistance in ${loc.name} | Travel Passport and Visa LLC`}
        siteName={SITE.siteName}
        description={`Passport and visa assistance for travelers in ${loc.name}. Professional document preparation from a private company with 20+ years of experience. Call 866-334-8472.`}
      />
      <section className="grain bg-navy py-16 lg:py-24" data-testid="location-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <span className="inline-block rounded-full border border-gold/40 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-gold" data-testid="location-private-badge">
            Private Service Provider
          </span>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl" data-testid="location-h1">
            <span className="mask-line">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: EASE }}>
                Passport and Visa Assistance
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.28, ease: EASE }}>
                for Travelers in <span className="text-gold">{loc.name}</span>
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45, ease: EASE }}
            className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            {loc.intro}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <GetStartedButton testId="location-get-started" />
            <CallButton light testId="location-call-now" />
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-[1.2fr_0.8fr]">
          <div>
            <Reveal>
              <h2 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
                Serving {loc.short} Travelers
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">{loc.context}</p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-200 p-5" data-testid="location-airport-card">
                  <Plane className="h-5 w-5 text-gold-dark" />
                  <p className="mt-3 font-display text-sm font-bold text-navy">International Departures</p>
                  <p className="mt-1 text-sm text-slate-600">{loc.airport}</p>
                </div>
                <div className="rounded-xl border border-slate-200 p-5" data-testid="location-agency-card">
                  <Building2 className="h-5 w-5 text-gold-dark" />
                  <p className="mt-3 font-display text-sm font-bold text-navy">
                    {loc.agency ? "Regional Passport Agency" : "Urgent Travel Options"}
                  </p>
                  <p className="mt-1 text-sm text-slate-600">
                    {loc.agency
                      ? `${loc.agency} — appointments are scheduled through the U.S. Department of State.`
                      : "Regional passport agency appointments are scheduled through the U.S. Department of State."}
                  </p>
                </div>
              </div>
              <div className="mt-8 rounded-xl border-l-4 border-gold bg-slate-50 p-6" data-testid="location-disclaimer-note">
                <p className="text-sm leading-relaxed text-slate-600">
                  <strong className="text-navy">Please note:</strong> {SITE.name} is a private service provider serving travelers in {loc.name} from our Washington, DC office. We are not a government agency. {SITE.processingDisclaimer}
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-12 font-display text-2xl font-bold tracking-tight text-navy">
                Services Available to {loc.short} Travelers
              </h2>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {SERVICES.slice(0, 8).map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    data-testid={`location-service-${s.slug}`}
                    className="group flex items-center gap-3 rounded-xl border border-slate-200 px-5 py-4 transition-colors hover:border-gold/60"
                  >
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-gold-dark" />
                    <span className="text-sm font-medium text-navy group-hover:text-gold-dark">{s.name}</span>
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
          <div>
            <Reveal delay={0.1}>
              <div id="get-started" className="scroll-mt-28 rounded-2xl bg-navy p-7 shadow-xl" data-testid="location-form-card">
                <div className="gold-rule mb-5 w-14" />
                <h2 className="font-display text-xl font-bold text-white">Tell Us What You Need Help With</h2>
                <p className="mb-6 mt-2 text-sm text-slate-400">Our team will follow up with next steps.</p>
                <LeadForm dark source={`location-${loc.slug}`} testId="location-lead-form" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-14" data-testid="location-nearby-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <h2 className="font-display text-xl font-bold text-navy">Other Service Areas</h2>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {nearby.map((l) => (
                <Link
                  key={l.slug}
                  to={`/locations/${l.slug}`}
                  data-testid={`location-nearby-${l.slug}`}
                  className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 transition-colors hover:border-gold hover:text-navy"
                >
                  <MapPin className="h-3.5 w-3.5 text-gold-dark" />
                  {l.name}
                </Link>
              ))}
              <Link to="/locations" data-testid="location-view-all" className="flex items-center gap-2 rounded-full border border-gold/50 px-4 py-2 text-sm font-semibold text-gold-dark transition-colors hover:bg-gold-pale/40">
                View All Areas
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="grain bg-navy-deep py-14" data-testid="location-bottom-cta">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-5 px-4 text-center sm:px-6">
          <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">Ready to Get Started?</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <GetStartedButton testId="location-bottom-get-started" />
            <a href={SITE.phoneTel} data-testid="location-bottom-call" className="inline-flex items-center gap-2.5 rounded-full border border-white/30 px-7 py-3.5 font-display text-sm font-semibold text-white transition-colors hover:border-gold hover:text-gold">
              <Phone className="h-4 w-4" /> CALL {SITE.phoneDisplay}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
